// compliance-engine.ts
// Motor normativo StorSafe — Anexo 1 MDT
// Genera checklist personalizado por empresa según DE 255, AM 196, Decisión 584, Resolución 957

import {
  perfilCompleto, figuraRequerida, modalidadPorDefecto, aplicaFiguras, paritarioRequerido,
  type ModalidadSST, type RiskLevel, type CompanySize,
} from './figuras-sst'
import { ITEMS_SP, PESO_SP, aplicaSP, type RespuestasServicios } from './servicios-permanentes'

export type { RiskLevel, CompanySize }
export type CompanyType = 'publica' | 'privada'

export interface PriorityGroups {
  adultos_mayores: boolean
  lactancia: boolean
  embarazadas: boolean
  discapacidad: boolean
  enfermedades_catastroficas: boolean
}

// Bloque 6: la figura SST (monitor/técnico) y el organismo paritario se calculan
// aquí con workers_count y risk_level (figuras-sst.ts), no con los valores
// guardados en company_sso_profile. Sin trabajadores o sin nivel de riesgo el
// motor no calcula los ítems que dependen de ellos (perfil incompleto).
export interface CompanyProfileForEngine {
  workers_count: number | null
  company_type: CompanyType
  risk_level: RiskLevel | null
  has_remote_workers: boolean
  has_priority_groups: boolean
  priority_groups: PriorityGroups
  has_high_risk_construction: boolean
  has_high_risk_electrical: boolean
  has_vehicles: boolean
  has_contractors: boolean
  work_centers_count: number
  /** Quién gestiona la SST (M14). null = la que exige el DE 255 Art. 18. */
  modalidad_sst?: ModalidadSST | null
  /** Preguntas opcionales de Servicios Permanentes (M17, bloque 7). null = sin responder. */
  servicios?: RespuestasServicios
  /** Lote G (M51): ¿la empresa transporta productos químicos peligrosos? (Perfil SSO). null = sin responder. */
  transporta_quimicos_peligrosos?: boolean | null
  /** Lote A: ¿la empresa registra productos químicos o bodegas? Lo cuenta quien arma el Checklist (no está en
   *  company_sso_profile). null o sin dato = no se sabe: GT 14 (GT-20) aplica. */
  tiene_quimicos_o_bodegas?: boolean | null
  /** Mantenimiento M1: activos de ventilación o extracción y máquinas o equipos de producción del inventario (no archivados).
   *  null o sin dato = no se sabe: GT 11 (GT-27), GT 27 (GT-28) y GT 28 (GT-29) aplican. */
  activos_ventilacion?: number | null
  activos_maquinas?: number | null
  /** M61 (TH 5): ¿algún puesto de trabajo opera vehículos o maquinaria? Lo cuenta quien arma el Checklist. null o sin dato =
   *  no se pudo leer (sin M61 en la base): TH 5 sigue el "tiene vehículos" del Perfil SSO, como antes. */
  puestos_operan_vehiculos?: boolean | null
}

export const RAZON_SIN_QUIMICOS = 'La empresa no registra productos químicos ni bodegas'
export const RAZON_SIN_VENTILACION = 'La empresa no registra sistemas de ventilación o extracción en el inventario de activos'
export const RAZON_SIN_MAQUINAS = 'La empresa no registra máquinas ni equipos de producción en el inventario de activos'
export const RAZON_SIN_PUESTOS_VEHICULOS = 'Ningún puesto de trabajo opera vehículos o maquinaria (Puestos de Trabajo)'
const sinActivos = (n: number | null | undefined) => n === 0

// Las 5 secciones reales del Anexo 1 (Lista de verificación SST del MDT).
// 'servicios_permanentes': SP-01 a SP-09 desde el bloque 7 (servicios-permanentes.ts).
export type SeccionAnexo =
  | 'gestion_administrativa'
  | 'gestion_tecnica'
  | 'talento_humano'
  | 'procedimientos_operativos'
  | 'servicios_permanentes'

// Categorías de Gestión Técnica según el Anexo 1 (asignadas por número legal:
// 1–4 identificación, 5 y 12–19 higiene, 6 ergonomía y psicosocial, 7–9 medidas,
// 10–11 y 20–30 condiciones, 31–36 señalización, 37–39 trabajos especiales).
export type CategoriaGT =
  | 'identificacion'
  | 'higiene'
  | 'ergonomia_psicosocial'
  | 'medidas'
  | 'condiciones'
  | 'senalizacion'
  | 'trabajos_especiales'

export interface ChecklistItem {
  id: string           // ej: 'GA-01' — id interno estable (checklist_item_estados.item_id); no cambiar
  seccion: 'gestion_administrativa' | 'gestion_tecnica' | 'talento_humano' | 'procedimientos_operativos' | 'servicios_permanentes'
  numero: number
  seccion_anexo: SeccionAnexo
  numero_legal: number | null   // número del ítem en el Anexo 1; null si no es un ítem del Anexo
  categoria_gt?: CategoriaGT    // solo en Gestión Técnica
  complementario: boolean       // obligación real que no es un ítem del Anexo 1 (o lo duplica)
  titulo: string
  descripcion: string
  normativa: string[]
  aplica: boolean
  razon_no_aplica?: string
  peso: number         // peso relativo para el score (suma total = 100 entre los que aplican)
  modulo_storsafe?: string  // módulo de StorSafe que cubre este ítem
  // 'no_aplica' = "No aplica" marcado a mano por el administrador (bloque 6, C; M15):
  // sale del denominador del puntaje. Distinto de aplica = false (N/A automático del perfil).
  estado?: 'pendiente' | 'en_proceso' | 'cumple' | 'no_cumple' | 'no_aplica'
  // Perfil incompleto (sin trabajadores o sin nivel de riesgo): el "aplica" de este
  // ítem depende de esos datos y no se calcula (aplica = false, fuera del puntaje).
  sin_calcular?: boolean
}

export interface ComplianceChecklist {
  items: ChecklistItem[]
  total_aplicables: number
  total_no_aplica: number
  /** Sin trabajadores o sin nivel de riesgo: el Checklist pide completar el Perfil SSO. */
  perfil_incompleto: boolean
  secciones: {
    gestion_administrativa: number
    gestion_tecnica: number
    talento_humano: number
    procedimientos_operativos: number
    servicios_permanentes: number
  }
}

// Metadatos de presentación del Anexo 1 por id interno. No intervienen en aplica(),
// pesos ni score. Mapeo según docs/auditoria/cobertura_anexo1_2026-09.md, sección 4.1:
// en GA, TH y PO el id interno coincide con el número legal; en GT no.
type MetadatosAnexo = Pick<ChecklistItem, 'seccion_anexo' | 'numero_legal' | 'categoria_gt' | 'complementario'>

function metadatosCoincidentes(prefijo: string, seccion: SeccionAnexo, desde: number, hasta: number): Record<string, MetadatosAnexo> {
  const out: Record<string, MetadatosAnexo> = {}
  for (let n = desde; n <= hasta; n++) {
    out[`${prefijo}-${String(n).padStart(2, '0')}`] = { seccion_anexo: seccion, numero_legal: n, complementario: false }
  }
  return out
}

function gt(numero_legal: number | null, categoria_gt: CategoriaGT, complementario = false): MetadatosAnexo {
  return { seccion_anexo: 'gestion_tecnica', numero_legal, categoria_gt, complementario }
}

export const ANEXO1_METADATOS: Record<string, MetadatosAnexo> = {
  ...metadatosCoincidentes('GA', 'gestion_administrativa', 1, 12),
  'GT-01': gt(1, 'identificacion'),
  'GT-02': gt(2, 'identificacion'),
  'GT-03': gt(3, 'identificacion'),
  'GT-04': gt(4, 'identificacion'),
  'GT-05': gt(5, 'higiene'),
  'GT-06': gt(6, 'ergonomia_psicosocial'),
  'GT-07': gt(7, 'medidas'),
  'GT-08': gt(8, 'medidas'),
  'GT-09': gt(37, 'trabajos_especiales'),
  'GT-10': gt(38, 'trabajos_especiales'),
  'GT-11': gt(39, 'trabajos_especiales'),
  'GT-12': gt(15, 'higiene'),
  'GT-13': gt(16, 'higiene'),
  // Lote G (M51): GT 17 con id nuevo (GT-17 ya es el desglose de agentes biológicos y GT-26 a GT-29 los usa
  // Mantenimiento, M50). Señalización con el id igual al número del Anexo (GT-35 es el GT 35 del lote A, no se usa).
  'GT-30': gt(17, 'higiene'),
  'GT-31': gt(31, 'senalizacion'),
  'GT-32': gt(32, 'senalizacion'),
  'GT-33': gt(33, 'senalizacion'),
  'GT-34': gt(34, 'senalizacion'),
  'GT-36': gt(36, 'senalizacion'),
  'GT-14': gt(9, 'medidas'),
  // Duplican el ítem legal 5 (GT-05) desglosado por tipo de agente.
  'GT-15': gt(null, 'higiene', true),
  'GT-16': gt(null, 'higiene', true),
  'GT-17': gt(null, 'higiene', true),
  // Lote A (M47): ítems del Anexo 1 que faltaban, con ids nuevos (GT-18 y GT-19 los usaron PDFs antiguos).
  'GT-20': gt(14, 'higiene'),
  'GT-21': gt(21, 'condiciones'),
  'GT-22': gt(22, 'condiciones'),
  'GT-23': gt(26, 'condiciones'),
  'GT-24': gt(30, 'condiciones'),
  'GT-25': gt(35, 'senalizacion'),
  // Mantenimiento M1 (M50): los calcula Inspecciones v2 con las inspecciones por activo firmadas.
  'GT-26': gt(10, 'condiciones'),
  'GT-27': gt(11, 'condiciones'),
  'GT-28': gt(27, 'condiciones'),
  'GT-29': gt(28, 'condiciones'),
  // Etapa 5a (M60): ids nuevos libres (GT-18/GT-19 los usaron PDFs antiguos; GT-20 a GT-36 ocupados o reservados).
  'GT-40': gt(13, 'higiene'),
  'GT-41': gt(18, 'higiene'),
  'GT-42': gt(19, 'higiene'),
  'GT-43': gt(20, 'condiciones'),
  'GT-44': gt(23, 'condiciones'),
  'GT-45': gt(24, 'condiciones'),
  'GT-46': gt(25, 'condiciones'),
  ...metadatosCoincidentes('TH', 'talento_humano', 1, 10),
  ...metadatosCoincidentes('PO', 'procedimientos_operativos', 1, 27),
  // Protocolo de acoso (MDT-2025-102): obligación real, pero no es un ítem del Anexo 1.
  'PO-28': { seccion_anexo: 'procedimientos_operativos', numero_legal: null, complementario: true },
  'PO-29': { seccion_anexo: 'procedimientos_operativos', numero_legal: null, complementario: true },
  ...metadatosCoincidentes('SP', 'servicios_permanentes', 1, 9),
}

export const RAZON_SIN_QUIMICOS_NI_TRANSPORTE = 'La empresa no registra productos químicos ni bodegas y declaró que no transporta productos químicos peligrosos'

/**
 * Perfil para el motor a partir de la fila de company_sso_profile. Sin fila, o
 * con trabajadores o riesgo en null, quedan en null (nada de valores por
 * defecto): el motor marca el perfil como incompleto (bloque 6).
 */
export function perfilParaMotor(fila: Record<string, any> | null | undefined): CompanyProfileForEngine {
  const f = fila ?? {}
  return {
    workers_count:              f.workers_count ?? null,
    company_type:               f.company_type ?? 'privada',
    risk_level:                 f.risk_level ?? null,
    modalidad_sst:              f.modalidad_sst ?? null,
    has_remote_workers:         f.has_remote_workers ?? false,
    has_priority_groups:        f.has_priority_groups ?? false,
    priority_groups:            f.priority_groups ?? {
      adultos_mayores: false, lactancia: false, embarazadas: false,
      discapacidad: false, enfermedades_catastroficas: false,
    },
    has_high_risk_construction: f.has_high_risk_construction ?? false,
    has_high_risk_electrical:   f.has_high_risk_electrical ?? false,
    has_vehicles:               f.has_vehicles ?? false,
    has_contractors:            f.has_contractors ?? false,
    work_centers_count:         f.work_centers_count ?? 1,
    transporta_quimicos_peligrosos: f.transporta_quimicos_peligrosos ?? null,
    servicios: {
      centro_a_mas_de_2km:               f.centro_a_mas_de_2km ?? null,
      tiene_servicio_cocina:             f.tiene_servicio_cocina ?? null,
      trabajo_con_sustancias_o_suciedad: f.trabajo_con_sustancias_o_suciedad ?? null,
      trabajadores_cambian_ropa:         f.trabajadores_cambian_ropa ?? null,
      tiene_campamentos:                 f.tiene_campamentos ?? null,
    },
  }
}

// Ítems cuyo "aplica" depende de los trabajadores o del nivel de riesgo. Con el
// perfil incompleto quedan sin calcular (bloque 6). GA-08 aplica siempre.
// SP-02 (comedor) depende de los cincuenta trabajadores (bloque 7).
export const ITEMS_DEPENDEN_DEL_PERFIL = [
  'GA-01', 'GA-02', 'GA-04', 'GA-05', 'GA-06', 'GA-07', 'GA-09', 'GA-10', 'GA-11',
  'GT-05', 'PO-05', 'PO-11', 'PO-12', 'PO-13', 'PO-24', 'PO-25', 'PO-26', 'PO-27', 'PO-29',
  'SP-02',
]
export const RAZON_PERFIL_INCOMPLETO = 'Sin calcular: completa en el Perfil SSO el número de trabajadores y el nivel de riesgo'

export function generateChecklist(profile: CompanyProfileForEngine): ComplianceChecklist {
  const completo = perfilCompleto(profile)
  // Con el perfil incompleto, estos dos valores solo permiten armar la lista: todos
  // los ítems que dependen de ellos se marcan después como sin calcular.
  const w = completo ? (profile.workers_count as number) : 0
  const riesgo: RiskLevel = completo ? (profile.risk_level as RiskLevel) : 'bajo'
  const figura = figuraRequerida(Math.max(w, 1), riesgo)
  const modalidad: ModalidadSST = profile.modalidad_sst ?? modalidadPorDefecto(Math.max(w, 1), riesgo)
  const fig = aplicaFiguras(modalidad, figura)
  const paritario = paritarioRequerido(Math.max(w, 1))
  const razonPorModalidad: Record<ModalidadSST, string> = {
    monitor: 'La empresa tiene monitor propio (DE 255 Art. 19); su registro es el ítem GA-04',
    tecnico: 'La empresa tiene técnico propio (DE 255 Art. 20); su registro es el ítem GA-05',
    servicio_externo: 'La empresa contrató servicio externo (DE 255 Art. 25); su registro es el ítem GA-06',
  }
  const itemsBase: Omit<ChecklistItem, keyof MetadatosAnexo>[] = [

    // ══════════════════════════════════════════════
    // SECCIÓN A — GESTIÓN ADMINISTRATIVA
    // ══════════════════════════════════════════════

    {
      id: 'GA-01',
      seccion: 'gestion_administrativa',
      numero: 1,
      titulo: 'Plan de Prevención de Riesgos Laborales',
      descripcion: 'Plan de prevención aprobado y registrado en el SUT (1 a 10 trabajadores)',
      normativa: ['AM 196 (2024) Art. 4 y 18', 'Decisión 584 (2004) Art. 11', 'Código del Trabajo Art. 434'],
      aplica: w >= 1 && w <= 10,
      razon_no_aplica: w > 10 ? 'Aplica Reglamento de Higiene (ítem GA-02) para más de 10 trabajadores' : undefined,
      peso: 5,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GA-02',
      seccion: 'gestion_administrativa',
      numero: 2,
      titulo: 'Reglamento de Higiene y Seguridad',
      descripcion: 'Reglamento aprobado y registrado en el SUT (más de 10 trabajadores)',
      normativa: ['Código del Trabajo Art. 434', 'AM 196 (2024) Art. 4 y 19'],
      aplica: w > 10,
      razon_no_aplica: w <= 10 ? 'Aplica Plan de Prevención (ítem GA-01) para 1 a 10 trabajadores' : undefined,
      peso: 5,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GA-03',
      seccion: 'gestion_administrativa',
      numero: 3,
      titulo: 'Política de seguridad y salud socializada',
      descripcion: 'Política SST socializada a todos los trabajadores',
      normativa: ['Decisión 584 (2004) Art. 11'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GA-04',
      seccion: 'gestion_administrativa',
      numero: 4,
      titulo: 'Registro Monitor SST en SUT',
      descripcion: 'Registro del Monitor de Seguridad e Higiene del Trabajo en la Plataforma SUT',
      normativa: ['DE 255 (2024) Art. 18 y 19', 'AM 196 (2024) Art. 18 y 19'],
      aplica: fig.ga04,
      razon_no_aplica: !fig.ga04 ? razonPorModalidad[modalidad] : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-05',
      seccion: 'gestion_administrativa',
      numero: 5,
      titulo: 'Registro Técnico SST en SUT',
      descripcion: 'Registro del Técnico de Seguridad e Higiene del Trabajo en la Plataforma SUT',
      normativa: ['DE 255 (2024) Art. 18 y 20', 'AM 196 (2024) Art. 18 y 19'],
      aplica: fig.ga05,
      razon_no_aplica: !fig.ga05 ? razonPorModalidad[modalidad] : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-06',
      seccion: 'gestion_administrativa',
      numero: 6,
      titulo: 'Registro Servicio Externo SST en SUT',
      descripcion: 'Registro del Servicio Externo de Seguridad e Higiene del Trabajo en la Plataforma SUT',
      normativa: ['DE 255 (2024) Art. 25', 'AM 196 (2024) Art. 14'],
      // Texto del Anexo 1, ítem 6. Sin umbral de trabajadores: depende de la modalidad (bloque 6, B).
      aplica: fig.ga06,
      razon_no_aplica: !fig.ga06 ? razonPorModalidad[modalidad] : undefined,
      peso: 3,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-07',
      seccion: 'gestion_administrativa',
      numero: 7,
      titulo: 'Informe de actividades del Técnico o Servicio Externo',
      descripcion: 'Informe con objetivo, estadísticas, actividades, conclusiones, fotos y firmas',
      normativa: ['AM 196 (2024) Art. 13 y 15'],
      // Informe mensual del técnico (Art. 13) o del servicio externo (Art. 15).
      aplica: fig.ga07,
      razon_no_aplica: !fig.ga07 ? 'El informe mensual lo remiten el técnico o el servicio externo (AM 196 Arts. 13 y 15); la empresa tiene monitor propio' : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GA-08',
      seccion: 'gestion_administrativa',
      numero: 8,
      titulo: 'Registro profesional médico en SUT',
      descripcion: 'Registro del profesional médico en la Plataforma SUT. DE 255 Art. 18: de 1 a 99 trabajadores, un profesional médico con formación de 4to nivel en seguridad y salud en el trabajo de visita periódica; de 100 en adelante, el médico y un profesional de enfermería; a partir de 300, además un profesional en psicología; a partir de 1000, un profesional médico con especialidad en Medicina del Trabajo',
      normativa: ['DE 255 (2024) Art. 18 y 21'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-09',
      seccion: 'gestion_administrativa',
      numero: 9,
      titulo: 'Registro Delegado SST en SUT',
      descripcion: 'Registro del Delegado de Seguridad y Salud en la Plataforma SUT',
      normativa: ['DE 255 (2024) Art. 33', 'AM 196 (2024) Art. 18 y 19'],
      // Con el total de trabajadores hasta F1 (por centro de trabajo: DE 255 Arts. 32-33, AM 196 Art. 21).
      aplica: paritario === 'delegado',
      razon_no_aplica: paritario !== 'delegado' ? `La empresa requiere ${paritario === 'comite' ? 'Comité (GA-10)' : 'Participación Directa'}` : undefined,
      peso: 3,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-10',
      seccion: 'gestion_administrativa',
      numero: 10,
      titulo: 'Registro Comité SST en SUT',
      descripcion: 'Registro del Comité de Seguridad y Salud en la Plataforma SUT',
      normativa: ['DE 255 (2024) Art. 32', 'AM 196 (2024) Art. 18 y 19'],
      aplica: paritario === 'comite',
      razon_no_aplica: paritario !== 'comite' ? `La empresa requiere ${paritario === 'delegado' ? 'Delegado (GA-09)' : 'Participación Directa'}` : undefined,
      peso: 3,
      modulo_storsafe: 'Documentos SSO', // registro en el SUT: se sube el comprobante (bloque 5b, G)
    },
    {
      id: 'GA-11',
      seccion: 'gestion_administrativa',
      numero: 11,
      titulo: 'Informe de gestión del Organismo Paritario',
      descripcion: 'Informe con cronograma, actividades Art. 39 DE 255, conclusiones, fotos y firmas',
      normativa: ['Resolución 957 (2008) Art. 10, 13, 14', 'DE 255 (2024) Art. 36, 38'],
      aplica: paritario !== 'participacion_directa',
      peso: 3,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GA-12',
      seccion: 'gestion_administrativa',
      numero: 12,
      titulo: 'Procedimientos para actividades simultáneas',
      descripcion: 'Procedimientos escritos para empleadores con contratistas o subcontratistas en el mismo lugar de trabajo',
      normativa: ['AM 196 (2024) Art. 4'],
      aplica: profile.has_contractors,
      razon_no_aplica: !profile.has_contractors ? 'La empresa no declara contratistas o subcontratistas' : undefined,
      peso: 2,
      modulo_storsafe: 'Documentos SSO',
    },

    // ══════════════════════════════════════════════
    // SECCIÓN B — GESTIÓN TÉCNICA
    // ══════════════════════════════════════════════

    {
      id: 'GT-01',
      seccion: 'gestion_tecnica',
      numero: 1,
      titulo: 'Diagrama de flujo de procesos',
      descripcion: 'Diagrama de flujo de todos los procesos productivos y/o de servicios',
      normativa: ['Decisión 584 Art. 11'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GT-02',
      seccion: 'gestion_tecnica',
      numero: 2,
      titulo: 'Descriptivo por puesto de trabajo',
      descripcion: 'Descriptivo por puesto de trabajo con número de trabajadores asignados, actividades realizadas, horas de actividad diaria, recursos utilizados y materiales/agentes químicos-biológicos',
      normativa: ['Decisión 584 Art. 11, 19', 'Código del Trabajo Art. 42', 'DE 255 (2024) Art. 28'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Puestos de Trabajo',
    },
    {
      id: 'GT-03',
      seccion: 'gestion_tecnica',
      numero: 3,
      titulo: 'Mapa de riesgos',
      descripcion: 'Mapa con señalización, EPP, dispositivos de parada de emergencia',
      normativa: ['Decisión 584 Art. 11'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GT-04',
      seccion: 'gestion_tecnica',
      numero: 4,
      titulo: 'Matriz de Riesgos por puesto de trabajo',
      descripcion: 'Identificación de peligros y evaluación de riesgos con metodología reconocida (GTC-45)',
      normativa: ['Decisión 584 Art. 11', 'Resolución 957 Art. 1', 'DE 255 (2024) Art. 27, 28, 47'],
      aplica: true,
      peso: 6,
      modulo_storsafe: 'Matriz de Riesgos',
    },
    {
      id: 'GT-05',
      seccion: 'gestion_tecnica',
      numero: 5,
      titulo: 'Informe de medición de agentes físicos, químicos y biológicos',
      descripcion: 'Informe con fecha, puesto, nº expuestos, metodología, resultados vs norma, firmas y calibración',
      normativa: ['Decisión 584 Art. 11, 12, 18', 'DE 255 (2024) Art. 48', 'AM 196 (2024)'],
      aplica: profile.risk_level === 'medio' || profile.risk_level === 'alto',
      razon_no_aplica: profile.risk_level === 'bajo' ? 'Recomendado pero no obligatorio para riesgo bajo sin agentes críticos identificados' : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GT-06',
      seccion: 'gestion_tecnica',
      numero: 6,
      titulo: 'Informe de evaluación de riesgos ergonómicos y psicosociales',
      descripcion: 'Informe por puesto con metodología, resultados vs norma, firmas',
      normativa: ['Decisión 584 Art. 11', 'DE 255 (2024) Art. 44, 45, 46', 'AM 196 (2024)'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'GT-07',
      seccion: 'gestion_tecnica',
      numero: 7,
      titulo: 'Informe de medidas de prevención implementadas',
      descripcion: 'Informe con cronograma, jerarquía de controles, resultados, seguimiento y firmas',
      normativa: ['Decisión 584 Art. 11', 'DE 255 (2024) Art. 49'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Inspecciones',
    },
    {
      id: 'GT-08',
      seccion: 'gestion_tecnica',
      numero: 8,
      titulo: 'Riesgo residual en matriz de riesgos',
      descripcion: 'Cálculo del riesgo residual luego de aplicar controles, registrado para todas las evaluaciones de la matriz de riesgos',
      normativa: ['Resolución 957 (2008) Art. 1'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Matriz de Riesgos',
    },
    {
      id: 'GT-09',
      seccion: 'gestion_tecnica',
      numero: 9,
      titulo: 'Procedimientos para trabajos especiales',
      descripcion: 'Procedimientos de SST documentados y ejecutados para los 9 tipos de trabajos especiales (altura, caliente, confinado, eléctrico, químico, LOTO, excavación, maquinaria, vías)',
      normativa: ['Decreto Ejecutivo 255 (2024) Art. 58', 'Decisión 584 (2004) Art. 11'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Permisos de Trabajo',
    },
    {
      id: 'GT-10',
      seccion: 'gestion_tecnica',
      numero: 10,
      titulo: 'Emisión de permisos de trabajo',
      descripcion: 'Permisos de trabajo emitidos formalmente conforme el procedimiento, con autorización y ejecución registradas',
      normativa: ['Decreto Ejecutivo 255 (2024) Art. 58'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Permisos de Trabajo',
    },
    {
      id: 'GT-11',
      seccion: 'gestion_tecnica',
      numero: 11,
      titulo: 'Registro de apertura y cierre de permisos de trabajo',
      descripcion: 'Registro del cierre formal del permiso con checklist de verificación completo',
      normativa: ['Decisión 584 (2004) Art. 11', 'Decreto Ejecutivo 255 (2024) Art. 58', 'Acuerdo Ministerial 174 (2017)', 'Acuerdo Ministerial 013 (2017)', 'AM MDT-2025-122 (2025)'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Permisos de Trabajo',
    },
    {
      id: 'GT-12',
      seccion: 'gestion_tecnica',
      numero: 12,
      titulo: 'Fichas de datos de seguridad accesibles',
      descripcion: 'Fichas de datos de seguridad (SDS/MSDS) disponibles y de fácil acceso para el trabajador, para todos los productos químicos activos',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Productos',
    },
    {
      id: 'GT-13',
      seccion: 'gestion_tecnica',
      numero: 13,
      titulo: 'Etiquetado de agentes químicos en español',
      descripcion: 'Etiquetas SGA generadas (con pictogramas GHS, palabra de señal, frases H/P) para todos los productos químicos activos',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Etiquetado SGA',
    },
    {
      id: 'GT-14',
      seccion: 'gestion_tecnica',
      numero: 14,
      titulo: 'Verificación in situ de medidas de prevención implementadas',
      descripcion: 'Inspección finalizada con checklist de verificación completamente documentado en campo',
      normativa: ['Decisión 584 (2004) Art. 11'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones',
    },
    // Lote A (M47): los calcula Inspecciones v2 con sus inspecciones firmadas (insp-anexo1.ts). Peso 3, como los demás GT.
    {
      id: 'GT-20',
      seccion: 'gestion_tecnica',
      numero: 20,
      titulo: 'Almacenamiento de agentes químicos según su compatibilidad',
      descripcion: '¿Se almacenan agentes químicos en áreas específicas, según su compatibilidad?',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: profile.tiene_quimicos_o_bodegas !== false,
      razon_no_aplica: profile.tiene_quimicos_o_bodegas === false ? RAZON_SIN_QUIMICOS : undefined,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Productos químicos)',
    },
    {
      id: 'GT-21',
      seccion: 'gestion_tecnica',
      numero: 21,
      titulo: 'Lugares de trabajo ordenados y limpios',
      descripcion: '¿Los lugares y/o centros de trabajo se encuentran ordenados y limpios?',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-22',
      seccion: 'gestion_tecnica',
      numero: 22,
      titulo: 'Iluminación mínima en áreas de circulación y pasillos',
      descripcion: '¿Las áreas de circulación y los pasillos cuentan con los niveles mínimos de iluminación requeridos? (mínimo 100 lux, Art. 10.4 del Anexo 3 AM 196)',
      normativa: ['AM 196 (2024) Anexo 3 Art. 10.4'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-23',
      seccion: 'gestion_tecnica',
      numero: 23,
      titulo: 'Estructuras de prevención contra caída de objetos y personas',
      descripcion: '¿La estructura de prevención contra caída de objetos y personas está en buen estado? (plataformas, barandillas, rodapiés, escaleras, eslingas y ganchos)',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-24',
      seccion: 'gestion_tecnica',
      numero: 24,
      titulo: 'Puertas y salidas señalizadas y libres de obstáculos',
      descripcion: '¿Las puertas y salidas se encuentran debidamente señalizadas y libres de obstáculos?',
      normativa: ['AM 196 (2024) Anexo 3'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-25',
      seccion: 'gestion_tecnica',
      numero: 25,
      titulo: 'Señalización de equipos contra incendio',
      descripcion: 'Señalización de los equipos contra incendio conforme la NTE INEN-ISO 3864-1:2013 (forma, color de seguridad y pictograma)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    // Mantenimiento M1 (M50): inspecciones por activo firmadas (plantillas de la biblioteca), mismo cálculo que el lote A.
    {
      id: 'GT-26',
      seccion: 'gestion_tecnica',
      numero: 26,
      titulo: 'Limpieza y mantenimiento periódico de luminarias',
      descripcion: '¿Se ha realizado la limpieza y mantenimiento periódico de luminarias en los lugares y/o centros de trabajo?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 10'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inventario de activos (inspecciones por activo)',
    },
    {
      id: 'GT-27',
      seccion: 'gestion_tecnica',
      numero: 27,
      titulo: 'Mantenimiento periódico de los sistemas de ventilación',
      descripcion: '¿Se ha realizado mantenimiento periódico de los sistemas de ventilación de los lugares y/o centros de trabajo?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 11 y Art. 46'],
      aplica: !sinActivos(profile.activos_ventilacion),
      razon_no_aplica: sinActivos(profile.activos_ventilacion) ? RAZON_SIN_VENTILACION : undefined,
      peso: 3,
      modulo_storsafe: 'Inventario de activos (inspecciones por activo)',
    },
    {
      id: 'GT-28',
      seccion: 'gestion_tecnica',
      numero: 28,
      titulo: 'Dispositivos y pulsadores de parada de emergencia',
      descripcion: '¿Los dispositivos de paradas, pulsadores de parada y dispositivos de parada de emergencia están perfectamente señalizados, fácilmente accesibles y están en un lugar seguro?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 65 y Art. 75'],
      aplica: !sinActivos(profile.activos_maquinas),
      razon_no_aplica: sinActivos(profile.activos_maquinas) ? RAZON_SIN_MAQUINAS : undefined,
      peso: 3,
      modulo_storsafe: 'Inventario de activos (inspecciones por activo)',
    },
    {
      id: 'GT-29',
      seccion: 'gestion_tecnica',
      numero: 29,
      titulo: 'Resguardos en partes fijas y móviles de motores y máquinas',
      descripcion: '¿Todas las partes fijas y móviles de motores, órganos de transmisión, máquinas, entre otros, se encuentran eficazmente protegidas mediante resguardos u otros dispositivos de seguridad?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 69 a Art. 73'],
      aplica: !sinActivos(profile.activos_maquinas),
      razon_no_aplica: sinActivos(profile.activos_maquinas) ? RAZON_SIN_MAQUINAS : undefined,
      peso: 3,
      modulo_storsafe: 'Inventario de activos (inspecciones por activo)',
    },
    // Etapa 5a (M60): los calcula Inspecciones v2 con la última inspección firmada y vigente (insp-anexo1.ts, regla del
    // lote A). Pregunta del Anexo 1 tal cual; aplicabilidad de GT 18, 19 y 25 decidida por StorSafe (no normativa): aplican
    // siempre y, si no hay agentes biológicos o rampas, la inspección responde "No aplica" y el cálculo lo sugiere.
    {
      id: 'GT-40',
      seccion: 'gestion_tecnica',
      numero: 40,
      titulo: 'Recipientes de agentes químicos con tapas o cubiertas adecuadas',
      descripcion: 'Los recipientes que contienen agentes químicos. ¿Cuentan con tapas o cubiertas adecuadas?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 11'],
      aplica: profile.tiene_quimicos_o_bodegas !== false,
      razon_no_aplica: profile.tiene_quimicos_o_bodegas === false ? RAZON_SIN_QUIMICOS : undefined,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Productos químicos)',
    },
    {
      id: 'GT-41',
      seccion: 'gestion_tecnica',
      numero: 41,
      titulo: 'Medidas de bioseguridad para agentes biológicos',
      descripcion: '¿Se aplican medidas de bioseguridad para la prevención y control de agentes biológicos?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-42',
      seccion: 'gestion_tecnica',
      numero: 42,
      titulo: 'Área para almacenamiento y disposición de desechos biológicos',
      descripcion: '¿Se ha dispuesto un área específica para el almacenamiento y disposición de desechos biológicos, según los lineamientos de la autoridad competente?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-43',
      seccion: 'gestion_tecnica',
      numero: 43,
      titulo: 'Control de plagas y/o vectores',
      descripcion: '¿Se ha implementado mecanismos de control de plagas y/o vectores en el lugar y/o centro de trabajo?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-44',
      seccion: 'gestion_tecnica',
      numero: 44,
      titulo: 'Áreas de circulación de personal y/o vehículos delimitadas',
      descripcion: '¿Se han delimitado las áreas para la circulación del personal y/o vehículos en el lugar y/o centro de trabajo?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 50 y Art. 53'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-45',
      seccion: 'gestion_tecnica',
      numero: 45,
      titulo: 'Áreas de emplazamiento de máquinas delimitadas',
      descripcion: '¿Se han delimitado las áreas para emplazamiento de máquinas en el lugar y/o centro de trabajo?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 34 y Art. 50'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },
    {
      id: 'GT-46',
      seccion: 'gestion_tecnica',
      numero: 46,
      titulo: 'Rampas conforme la norma',
      descripcion: '¿Las rampas están diseñadas conforme establece la norma?',
      normativa: ['AM 196 (2024) Anexo 3 Art. 51'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Condiciones generales)',
    },

    // ══════════════════════════════════════════════
    // SECCIÓN C — GESTIÓN DEL TALENTO HUMANO
    // ══════════════════════════════════════════════

    {
      id: 'TH-01',
      seccion: 'talento_humano',
      numero: 1,
      titulo: 'Identificación de grupos de atención prioritaria',
      descripcion: 'Identificación en evaluaciones de riesgo: adultos mayores, lactancia, embarazadas, discapacidad, enfermedades catastróficas',
      normativa: ['Constitución Art. 35', 'Decisión 584 Art. 11, 18, 25', 'Ley Orgánica Discapacidades Art. 16, 19, 45, 52'],
      aplica: profile.has_priority_groups,
      razon_no_aplica: !profile.has_priority_groups ? 'La empresa no declara trabajadores en grupos de atención prioritaria' : undefined,
      peso: 3,
      modulo_storsafe: 'Trabajadores',
    },
    {
      id: 'TH-02',
      seccion: 'talento_humano',
      numero: 2,
      titulo: 'Medidas de prevención para grupos prioritarios',
      descripcion: 'Evidencia in situ de medidas implementadas para grupos de atención prioritaria',
      normativa: ['Decisión 584 Art. 11, 27', 'DE 255 (2024) Art. 15'],
      aplica: profile.has_priority_groups,
      razon_no_aplica: !profile.has_priority_groups ? 'La empresa no declara trabajadores en grupos de atención prioritaria' : undefined,
      peso: 3,
      modulo_storsafe: 'Trabajadores',
    },
    {
      id: 'TH-03',
      seccion: 'talento_humano',
      numero: 3,
      titulo: 'Certificación trabajos de alto riesgo — Construcción',
      descripcion: 'Certificación de prevención de riesgos en actividades de construcción',
      normativa: ['AM 174 (2017)', 'DE 255 (2024) Art. 15', 'AM MDT-2025-122 (2025)'],
      aplica: profile.has_high_risk_construction,
      razon_no_aplica: !profile.has_high_risk_construction ? 'La empresa no declara actividades de construcción' : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'TH-04',
      seccion: 'talento_humano',
      numero: 4,
      titulo: 'Certificación trabajos de alto riesgo — Energía eléctrica',
      descripcion: 'Certificación de prevención de riesgos en actividades eléctricas',
      normativa: ['AM 013 (2017)', 'DE 255 (2024) Art. 15'],
      aplica: profile.has_high_risk_electrical,
      razon_no_aplica: !profile.has_high_risk_electrical ? 'La empresa no declara actividades eléctricas de alto riesgo' : undefined,
      peso: 4,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'TH-05',
      seccion: 'talento_humano',
      numero: 5,
      titulo: 'Licencias de conducción del personal',
      descripcion: 'Personal que opera vehículos cuenta con licencia acorde a su categoría',
      normativa: ['Reglamento Transporte Terrestre Art. 132', 'DE 255 (2024) Art. 51'],
      // M61: aplica si algún puesto opera vehículos o maquinaria; sin el dato, el Perfil SSO (como antes).
      aplica: profile.puestos_operan_vehiculos ?? profile.has_vehicles,
      razon_no_aplica: profile.puestos_operan_vehiculos === false ? RAZON_SIN_PUESTOS_VEHICULOS
        : profile.puestos_operan_vehiculos == null && !profile.has_vehicles ? 'La empresa no declara operación de vehículos a motor' : undefined,
      peso: 2,
      modulo_storsafe: 'Trabajadores',
    },
    {
      id: 'TH-06',
      seccion: 'talento_humano',
      numero: 6,
      titulo: 'Registro de asistencia a inducciones SST',
      descripcion: 'Registro con fecha, tema, nombres, cédulas, firmas, material y evaluación de conocimientos',
      normativa: ['Decisión 584 Art. 11, 23', 'DE 255 (2024) Art. 15'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Capacitaciones',
    },
    {
      id: 'TH-07',
      seccion: 'talento_humano',
      numero: 7,
      titulo: 'Campañas de comunicación SST',
      descripcion: 'Respaldos físicos o digitales de campañas de comunicación en SST',
      normativa: ['Decisión 584 Art. 19', 'Resolución 957 Art. 1'],
      aplica: true,
      peso: 2,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'TH-08',
      seccion: 'talento_humano',
      numero: 8,
      titulo: 'Programa de formación y capacitación SST',
      descripcion: 'Programa con objetivos, diagnóstico, contenido, cronograma, metodología, responsables y firmas',
      normativa: ['Decisión 584 Art. 11, 23', 'DE 255 Art. 15, 16, 28', 'AM 196 Art. 4'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Capacitaciones',
    },
    {
      id: 'TH-09',
      seccion: 'talento_humano',
      numero: 9,
      titulo: 'Registro de asistencia a capacitaciones',
      descripcion: 'Registro con fecha, tema, nombres, cédulas, firmas, material y evaluación',
      normativa: ['Decisión 584 Art. 11, 23', 'DE 255 Art. 15, 16, 28'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Capacitaciones',
    },
    {
      id: 'TH-10',
      seccion: 'talento_humano',
      numero: 10,
      titulo: 'Capacitaciones registradas en SUT',
      descripcion: 'Registro de capacitaciones en la plataforma SUT del Ministerio del Trabajo',
      normativa: ['Decisión 584 Art. 11, 23', 'DE 255 Art. 15, 16, 28'],
      aplica: true,
      peso: 2,
      modulo_storsafe: 'Capacitaciones',
    },

    // ══════════════════════════════════════════════
    // SECCIÓN D — PROCEDIMIENTOS OPERATIVOS BÁSICOS
    // ══════════════════════════════════════════════

    {
      id: 'PO-01',
      seccion: 'procedimientos_operativos',
      numero: 1,
      titulo: 'Matriz de exámenes médico ocupacionales',
      descripcion: 'Matriz por puesto: nº expuestos, riesgo, tipo examen, frecuencia, responsable y firmas',
      normativa: ['Decisión 584 Art. 14, 22', 'Resolución 957 Art. 5', 'DE 255 (2024) Art. 15'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Vigilancia Salud',
    },
    {
      id: 'PO-02',
      seccion: 'procedimientos_operativos',
      numero: 2,
      titulo: 'Cronograma de exámenes médico ocupacionales',
      descripcion: 'Planificación y ejecución de exámenes médicos por puesto',
      normativa: ['Decisión 584 Art. 14, 22', 'DE 255 (2024) Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Vigilancia Salud',
    },
    {
      id: 'PO-03',
      seccion: 'procedimientos_operativos',
      numero: 3,
      titulo: 'Informe de resultados de exámenes médicos',
      descripcion: 'Informe con periodo, puestos, nº exámenes, tipo, resultados estadísticos, acciones y firmas',
      normativa: ['Decisión 584 Art. 14, 22', 'DE 255 (2024) Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Vigilancia Salud',
    },
    {
      id: 'PO-04',
      seccion: 'procedimientos_operativos',
      numero: 4,
      titulo: 'Certificados de aptitud médica laboral',
      descripcion: 'Certificados de ingreso y periódicos con firmas del trabajador y médico',
      normativa: ['Decisión 584 Art. 14, 22', 'DE 255 (2024) Art. 15'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Vigilancia Salud',
    },
    {
      id: 'PO-05',
      seccion: 'procedimientos_operativos',
      numero: 5,
      titulo: 'Informe trimestral de indicadores de salud',
      descripcion: 'Informe con periodo, indicadores de enfermedad común, profesional y accidentes, conclusiones y firmas',
      normativa: ['Resolución 957 Art. 5', 'DE 255 (2024) Art. 15'],
      aplica: w >= 50,
      razon_no_aplica: w < 50 ? 'Obligatorio a partir de 50 trabajadores con médico ocupacional' : undefined,
      peso: 3,
      modulo_storsafe: 'Vigilancia Salud',
    },
    {
      id: 'PO-06',
      seccion: 'procedimientos_operativos',
      numero: 6,
      titulo: 'Procedimiento de investigación de accidentes',
      descripcion: 'Procedimiento documentado y aprobado con objetivos, alcance, responsabilidades, investigación, acciones y registros',
      normativa: ['Decisión 584 Art. 11', 'Resolución IESS CD 513 Art. 56'],
      aplica: true,
      peso: 4,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-07',
      seccion: 'procedimientos_operativos',
      numero: 7,
      titulo: 'Registro interno de incidentes y accidentes',
      descripcion: 'Registro con fecha, hora, trabajador, puesto, lugar, descripción y consecuencias',
      normativa: ['Decisión 584 Art. 1', 'Resolución IESS CD 513 Art. 47'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-08',
      seccion: 'procedimientos_operativos',
      numero: 8,
      titulo: 'Informe de investigación de accidentes',
      descripcion: 'Informe con fecha, lugar, trabajador, puesto, descripción, testigos, causas, acciones y firmas',
      normativa: ['Resolución IESS CD 513 Art. 47'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-09',
      seccion: 'procedimientos_operativos',
      numero: 9,
      titulo: 'Reporte de accidente a autoridad competente',
      descripcion: 'Reporte del accidente de trabajo al IESS y al Ministerio del Trabajo',
      normativa: ['Resolución IESS CD 513 Art. 44'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-10',
      seccion: 'procedimientos_operativos',
      numero: 10,
      titulo: 'Medidas correctivas post-accidente',
      descripcion: 'Medidas de control implementadas para evitar nuevos accidentes',
      normativa: ['Resolución IESS CD 513 Art. 53'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-11',
      seccion: 'procedimientos_operativos',
      numero: 11,
      titulo: 'Procedimiento de investigación de enfermedades profesionales',
      descripcion: 'Procedimiento documentado aprobado por la máxima autoridad',
      normativa: ['Decisión 584 Art. 11', 'Resolución IESS CD 513 Art. 47'],
      aplica: profile.risk_level === 'medio' || profile.risk_level === 'alto',
      razon_no_aplica: profile.risk_level === 'bajo' ? 'Recomendado pero no obligatorio para riesgo bajo sin agentes que generen enfermedad profesional' : undefined,
      peso: 3,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-12',
      seccion: 'procedimientos_operativos',
      numero: 12,
      titulo: 'Reporte de presunción de enfermedad profesional',
      descripcion: 'Reporte a la autoridad competente de presunción de enfermedad profesional',
      normativa: ['Resolución IESS CD 513 Art. 45'],
      aplica: profile.risk_level === 'medio' || profile.risk_level === 'alto',
      razon_no_aplica: profile.risk_level === 'bajo' ? 'Recomendado pero no obligatorio para riesgo bajo sin agentes que generen enfermedad profesional' : undefined,
      peso: 2,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-13',
      seccion: 'procedimientos_operativos',
      numero: 13,
      titulo: 'Medidas correctivas post-enfermedad profesional',
      descripcion: 'Medidas implementadas para evitar nuevos casos de enfermedad profesional',
      normativa: ['Resolución IESS CD 513 Art. 53'],
      aplica: profile.risk_level === 'medio' || profile.risk_level === 'alto',
      razon_no_aplica: profile.risk_level === 'bajo' ? 'Recomendado pero no obligatorio para riesgo bajo sin agentes que generen enfermedad profesional' : undefined,
      peso: 2,
      modulo_storsafe: 'Incidentes',
    },
    {
      id: 'PO-14',
      seccion: 'procedimientos_operativos',
      numero: 14,
      titulo: 'Programa anual de inspecciones internas SST',
      descripcion: 'Programa con objetivos, alcance, cronograma, áreas, lista de verificación y firmas',
      normativa: ['DE 255 (2024) Art. 28 num. 4', 'DE 255 (2024) Art. 39 num. 4'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones',
    },
    {
      id: 'PO-15',
      seccion: 'procedimientos_operativos',
      numero: 15,
      titulo: 'Ejecución de inspecciones internas con medidas correctivas',
      descripcion: 'Evidencia in situ de inspecciones ejecutadas y medidas correctivas implementadas',
      normativa: ['DE 255 (2024) Art. 28 num. 4', 'DE 255 (2024) Art. 39 num. 4'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones',
    },
    {
      id: 'PO-16',
      seccion: 'procedimientos_operativos',
      numero: 16,
      titulo: 'Plan de emergencias y contingencia',
      descripcion: 'Plan con objetivos, amenazas, procedimientos, mapa recursos, evacuación, simulacros, brigadas y firmas',
      normativa: ['Decisión 584 Art. 16', 'DE 255 (2024) Art. 58', 'Reglamento contra incendios Art. 17'],
      aplica: true,
      peso: 5,
      modulo_storsafe: 'Plan de Emergencias',
    },
    {
      id: 'PO-17',
      seccion: 'procedimientos_operativos',
      numero: 17,
      titulo: 'Informe anual de simulacros',
      descripcion: 'Informe con fecha, objetivo, tipo, lugar, duración, participantes, roles, incidencias y fotos',
      normativa: ['Decisión 584 Art. 11, 23', 'DE 255 (2024) Art. 15', 'AM 196 (2024) Art. 4'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Plan de Emergencias',
    },
    {
      id: 'PO-18',
      seccion: 'procedimientos_operativos',
      numero: 18,
      titulo: 'Implementación del plan de emergencia in situ',
      descripcion: 'Evidencia de implementación de acciones del plan de emergencia',
      normativa: ['Decisión 584 Art. 11, 23', 'AM 196 Art. 4'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Plan de Emergencias',
    },
    {
      id: 'PO-19',
      seccion: 'procedimientos_operativos',
      numero: 19,
      titulo: 'Programa de mantenimiento de instalaciones y equipos',
      descripcion: 'Programa con objetivos, inventario, cronograma, frecuencia, responsabilidades y protocolos',
      normativa: ['DE 255 (2024) Art. 50'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'PO-20',
      seccion: 'procedimientos_operativos',
      numero: 20,
      titulo: 'Ejecución del programa de mantenimiento',
      descripcion: 'Evidencia in situ de mantenimiento ejecutado en instalaciones, vehículos y equipos',
      normativa: ['DE 255 (2024) Art. 50'],
      aplica: true,
      peso: 2,
      modulo_storsafe: 'Documentos SSO',
    },
    {
      id: 'PO-21',
      seccion: 'procedimientos_operativos',
      numero: 21,
      titulo: 'Procedimiento de adquisición de EPP',
      descripcion: 'Procedimiento con objetivos, identificación de necesidades, matriz EPP por puesto y firmas',
      normativa: ['Decisión 584 Art. 11', 'DE 255 (2024) Art. 56'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'EPP',
    },
    {
      id: 'PO-22',
      seccion: 'procedimientos_operativos',
      numero: 22,
      titulo: 'Registro de entrega-recepción de EPP',
      descripcion: 'Registro con fecha, trabajador, cédula, detalle EPP, firmas y devoluciones',
      normativa: ['Decisión 584 Art. 11', 'DE 255 (2024) Art. 56'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'EPP',
    },
    {
      id: 'PO-23',
      seccion: 'procedimientos_operativos',
      numero: 23,
      titulo: 'Uso correcto de EPP verificado in situ',
      descripcion: 'Evidencia de uso correcto de EPP y ropa de trabajo por parte de los trabajadores',
      normativa: ['Decisión 584 Art. 11', 'DE 255 (2024) Art. 56'],
      aplica: true,
      peso: 2,
      modulo_storsafe: 'EPP',
    },
    {
      id: 'PO-24',
      seccion: 'procedimientos_operativos',
      numero: 24,
      titulo: 'Programa de prevención de riesgo psicosocial',
      descripcion: 'Programa implementado con 12 actividades respaldadas',
      normativa: ['MDT-2025-102 Art. 6 (reformado por MDT-2025-186)', 'AM 398 VIH-SIDA'],
      aplica: w > 10,
      razon_no_aplica: w <= 10 ? 'Implementación formal obligatoria a partir de 11 trabajadores; empresas más pequeñas igual deben gestionar prevención de riesgos psicosociales de forma general (MDT-2025-102 Art. 6)' : undefined,
      peso: 4,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'PO-25',
      seccion: 'procedimientos_operativos',
      numero: 25,
      titulo: 'Registro del programa psicosocial en SUT',
      descripcion: 'Registro del programa de prevención de riesgo psicosocial en la plataforma SUT',
      normativa: ['MDT-2025-102 Disposición General Quinta (reformado por MDT-2025-186)'],
      aplica: w > 10,
      razon_no_aplica: w <= 10 ? 'Solo aplica si se implementa el Programa formal (a partir de 11 trabajadores)' : undefined,
      peso: 2,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'PO-26',
      seccion: 'procedimientos_operativos',
      numero: 26,
      titulo: 'Programa de prevención alcohol, tabaco y drogas',
      descripcion: 'Programa implementado con 14 actividades respaldadas',
      normativa: ['Acuerdo Interministerial MDT-MSP-2019-038 Art. 2'],
      aplica: w > 10,
      razon_no_aplica: w <= 10 ? 'Obligatorio a partir de 11 trabajadores (Acuerdo Interministerial MDT-MSP-2019-038 Art. 2)' : undefined,
      peso: 3,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'PO-27',
      seccion: 'procedimientos_operativos',
      numero: 27,
      titulo: 'Registro programa alcohol/drogas en SUT',
      descripcion: 'Registro del programa de prevención de alcohol, tabaco y drogas en SUT',
      normativa: ['Acuerdo Interministerial MDT-MSP-2019-038 Art. 10'],
      aplica: w > 10,
      razon_no_aplica: w <= 10 ? 'Solo aplica si se implementa el programa formal (a partir de 11 trabajadores)' : undefined,
      peso: 2,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'PO-28',
      seccion: 'procedimientos_operativos',
      numero: 28,
      titulo: 'Protocolo Interno de Prevención y Erradicación de Discriminación, Violencia y Acoso Laboral',
      descripcion: 'Protocolo elaborado con los 4 ejes: prevención, identificación/detección, atención, seguimiento y evaluación',
      normativa: ['MDT-2025-102 Art. 6, 10 (reformado por MDT-2025-186)'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'PO-29',
      seccion: 'procedimientos_operativos',
      numero: 29,
      titulo: 'Registro del Protocolo Interno en plataforma MDT',
      descripcion: 'Registro y aprobación del Protocolo Interno en la plataforma informática del Ministerio del Trabajo',
      normativa: ['MDT-2025-102 Art. 11 (reformado por MDT-2025-186)'],
      aplica: w >= 10,
      razon_no_aplica: w < 10 ? 'Registro obligatorio solo a partir de 10 trabajadores (MDT-2025-186)' : undefined,
      peso: 2,
      modulo_storsafe: 'Riesgo Psicosocial',
    },
    {
      id: 'GT-15',
      seccion: 'gestion_tecnica',
      numero: 15,
      titulo: 'Higiene Industrial — Agentes Físicos',
      descripcion: 'Mediciones de agentes físicos (ruido, iluminación, vibración, etc.) por tarea',
      normativa: ['DE 255 (2024)', 'AM 196 (2024)'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Constructor Gestión Técnica',
    },
    {
      id: 'GT-16',
      seccion: 'gestion_tecnica',
      numero: 16,
      titulo: 'Higiene Industrial — Agentes Químicos',
      descripcion: 'Mediciones de agentes químicos por tarea',
      normativa: ['DE 255 (2024)', 'AM 196 (2024)'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Constructor Gestión Técnica',
    },
    {
      id: 'GT-17',
      seccion: 'gestion_tecnica',
      numero: 17,
      titulo: 'Higiene Industrial — Agentes Biológicos',
      descripcion: 'Mediciones de agentes biológicos por tarea',
      normativa: ['DE 255 (2024)', 'AM 196 (2024)'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Constructor Gestión Técnica',
    },
    // Lote G (M51): GT 17 desde las inspecciones firmadas de almacenamiento y de transporte (insp-lote-g.ts).
    {
      id: 'GT-30',
      seccion: 'gestion_tecnica',
      numero: 30,
      titulo: 'Transporte, almacenamiento y manejo de productos químicos',
      descripcion: '¿El transporte, almacenamiento y manejo de productos químicos se realiza conforme la NTE INEN 2266:2013? (inspección firmada de almacenamiento y, si la empresa transporta, de transporte)',
      normativa: ['NTE INEN 2266:2013'],
      aplica: profile.tiene_quimicos_o_bodegas !== false || profile.transporta_quimicos_peligrosos === true,
      razon_no_aplica: profile.tiene_quimicos_o_bodegas === false && profile.transporta_quimicos_peligrosos !== true
        ? (profile.transporta_quimicos_peligrosos === false ? RAZON_SIN_QUIMICOS_NI_TRANSPORTE : RAZON_SIN_QUIMICOS) : undefined,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Productos químicos)',
    },
    // Señalización (M51): GT 31, 32, 33, 34 y 36 desde la inspección firmada de señalización (insp-lote-g.ts).
    {
      id: 'GT-31',
      seccion: 'gestion_tecnica',
      numero: 31,
      titulo: 'Señalización preventiva',
      descripcion: '¿Existe señalización preventiva conforme la NTE INEN-ISO 3864-1:2013? (donde hay riesgo, visible, con la forma, el color de seguridad y el pictograma que corresponden)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Señalización)',
    },
    {
      id: 'GT-32',
      seccion: 'gestion_tecnica',
      numero: 32,
      titulo: 'Señalización prohibitiva',
      descripcion: '¿Existe señalización prohibitiva conforme la NTE INEN-ISO 3864-1:2013? (donde hay riesgo, visible, con la forma, el color de seguridad y el pictograma que corresponden)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Señalización)',
    },
    {
      id: 'GT-33',
      seccion: 'gestion_tecnica',
      numero: 33,
      titulo: 'Señalización de información',
      descripcion: '¿Existe señalización de información conforme la NTE INEN-ISO 3864-1:2013? (donde se necesita, visible, con la forma, el color de seguridad y el pictograma que corresponden)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Señalización)',
    },
    {
      id: 'GT-34',
      seccion: 'gestion_tecnica',
      numero: 34,
      titulo: 'Señalización de obligación',
      descripcion: '¿Existe señalización de obligación conforme la NTE INEN-ISO 3864-1:2013? (donde se exige una acción o un EPP, visible, con la forma, el color de seguridad y el pictograma que corresponden)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Señalización)',
    },
    {
      id: 'GT-36',
      seccion: 'gestion_tecnica',
      numero: 36,
      titulo: 'Señalización de evacuación en emergencias',
      descripcion: '¿Existe señalización que oriente la evacuación en caso de emergencia conforme la NTE INEN-ISO 3864-1:2013? (rutas y salidas señalizadas hasta el punto de encuentro)',
      normativa: ['NTE INEN-ISO 3864-1:2013'],
      aplica: true,
      peso: 3,
      modulo_storsafe: 'Inspecciones v2 (Señalización)',
    },
  ]

  // ══════════════════════════════════════════════
  // SECCIÓN E — SERVICIOS PERMANENTES (bloque 7)
  // ══════════════════════════════════════════════
  // Peso 3 cada uno (opción A de JC, 23/10/2026): se reescalan con los demás.
  // "Sin calcular" (pregunta del perfil sin responder) no cuenta en el puntaje.
  const respuestas = profile.servicios ?? {}
  for (const sp of ITEMS_SP) {
    const a = aplicaSP(sp, respuestas, w)
    itemsBase.push({
      id: sp.id,
      seccion: 'servicios_permanentes',
      numero: sp.numero_legal,
      titulo: sp.titulo,
      descripcion: sp.descripcion,
      normativa: sp.normativa,
      aplica: a.aplica,
      razon_no_aplica: a.razon_no_aplica,
      sin_calcular: a.sin_calcular,
      peso: PESO_SP,
      modulo_storsafe: 'Documentos SSO',
    })
  }

  // Mismo orden y mismos objetos: solo se agregan los metadatos de presentación.
  const items: ChecklistItem[] = itemsBase.map(i => Object.assign(i, ANEXO1_METADATOS[i.id]))

  // Perfil incompleto: nada de valores por defecto (decisión de JC, 23/10/2026).
  if (!completo) {
    items.forEach(i => {
      if (!ITEMS_DEPENDEN_DEL_PERFIL.includes(i.id)) return
      i.aplica = false
      i.sin_calcular = true
      i.razon_no_aplica = RAZON_PERFIL_INCOMPLETO
    })
  }

  // Redistribuir pesos: solo entre ítems que aplican
  const aplicables = items.filter(i => i.aplica)
  const totalPesoActual = aplicables.reduce((acc, i) => acc + i.peso, 0)
  const factor = 100 / totalPesoActual
  aplicables.forEach(i => { i.peso = Math.round(i.peso * factor * 10) / 10 })

  const secciones = {
    gestion_administrativa: aplicables.filter(i => i.seccion === 'gestion_administrativa').length,
    gestion_tecnica: aplicables.filter(i => i.seccion === 'gestion_tecnica').length,
    talento_humano: aplicables.filter(i => i.seccion === 'talento_humano').length,
    procedimientos_operativos: aplicables.filter(i => i.seccion === 'procedimientos_operativos').length,
    servicios_permanentes: aplicables.filter(i => i.seccion === 'servicios_permanentes').length,
  }

  return {
    items,
    total_aplicables: aplicables.length,
    total_no_aplica: items.length - aplicables.length,
    perfil_incompleto: !completo,
    secciones,
  }
}

// Helper: score del checklist (0-100) basado en estado de cada ítem. Un ítem en
// "No aplica" manual (bloque 6, C) sale del denominador: ni suma ni resta.
export function scoreChecklist(checklist: ComplianceChecklist): number {
  const cuentan = checklist.items.filter(i => i.aplica && i.estado !== 'no_aplica')
  const total = cuentan.reduce((acc, i) => acc + i.peso, 0)
  if (cuentan.length === 0 || total === 0) return 0
  const earned = cuentan
    .filter(i => i.estado === 'cumple')
    .reduce((acc, i) => acc + i.peso, 0)
  return Math.round(earned * 100 / total)
}

// Helper: ítems críticos sin cumplir (estado no_cumple o pendiente, con peso alto)
export function getCriticalGaps(checklist: ComplianceChecklist): ChecklistItem[] {
  return checklist.items
    .filter(i => i.aplica && i.estado !== 'cumple' && i.estado !== 'no_aplica')
    .sort((a, b) => b.peso - a.peso)
    .slice(0, 5)
}

export interface IncidentForCompliance {
  incident_type: string
  iess_reported: boolean | null
  corrective_actions: string | null
  requires_investigation: boolean | null
  investigator_id: string | null
  investigation_date: string | null
  immediate_cause: string | null
  witnesses: any[] | null
}

export function evaluateIncidentAutoCompliance(incidents: IncidentForCompliance[]): string[] {
  const cumple: string[] = []
  const noEnfermedad = incidents.filter(i => i.incident_type !== 'enfermedad_profesional')
  const enfermedad = incidents.filter(i => i.incident_type === 'enfermedad_profesional')

  if (incidents.length > 0) cumple.push('PO-07')

  const investigacionCompleta = incidents.some(i =>
    i.requires_investigation &&
    !!i.investigator_id &&
    !!i.investigation_date &&
    !!i.immediate_cause &&
    !!i.corrective_actions &&
    Array.isArray(i.witnesses) && i.witnesses.length > 0
  )
  if (investigacionCompleta) cumple.push('PO-08')

  if (noEnfermedad.some(i => i.iess_reported)) cumple.push('PO-09')
  if (noEnfermedad.some(i => !!i.corrective_actions)) cumple.push('PO-10')
  if (enfermedad.some(i => i.iess_reported)) cumple.push('PO-12')
  if (enfermedad.some(i => !!i.corrective_actions)) cumple.push('PO-13')

  return cumple
}
